import { Injectable } from '@angular/core';
import {environment} from '../../../environments/environment';
import {delay, map, repeatWhen, switchMap, take, takeLast, takeWhile, tap} from 'rxjs/operators';
import * as dayjs from 'dayjs';
import {EMPTY, Observable} from 'rxjs';
import {ScgHttpWrapper} from '@app/core/injectables/scg-http-wrapper';
import { DeviceType } from '../models/device';

/**
 * API endpoints to control sync.
 *
 * Note: Each device offers additional sync functions in it's model (`@app.core.models.device.Device`)
 */
@Injectable({
  providedIn: 'root'
})
export class SyncService {

  constructor(
    private scgHttp: ScgHttpWrapper
  ) { }

  public getIsOnlineCC(deviceId: string) {
    return this.scgHttp.withDevice(deviceId).get<{success: boolean, id?: string}>(environment.apiUrl + 'sse/isonline');
  }

  public getIsOnlineCB(deviceId: string) {
    return this.getLastSyncBoth(deviceId).pipe(
      map<string, boolean>(response => dayjs().diff(dayjs(response), 'second') < 140)
    );
  }

  public needSyncCb(deviceId: string): Observable<boolean> {
    return this.scgHttp.withDevice(deviceId).get<{ hasChanged: boolean }>(environment.apiUrl + 'cbsettings?key=hasChanged')
      .pipe(map(response => response.hasChanged));
  }

  public needSyncCc(deviceId: string): Observable<boolean> {
    return this.scgHttp.withDevice(deviceId).withUsername('allusers').get<{ hasChanged: boolean }>('ccsettings?key=hasChanged')
      .pipe(map(response => response.hasChanged));
  }

  public syncCb(deviceId: string): Observable<string> {
    return this.scgHttp.withDevice(deviceId).get<string>(environment.apiUrl + 'action/SyncNowAsync');
  }

  public syncCc(deviceId: string): Observable<{ success: boolean, id?: string }> {
    return this.scgHttp.withDevice(deviceId).get<{ success: boolean, id?: string }>(environment.apiUrl + 'sse/sync');
  }

  /** Trigger device sync and check whether sync was successful */
  public syncCbWithCallback(deviceId: string): Observable<SyncResult> {
    return this.syncWithCallback(deviceId, DeviceType.CB);
  }

  /** Trigger device sync and check whether sync was successful */
  public syncCcWithCallback(deviceId: string): Observable<SyncResult> {
    return this.syncWithCallback(deviceId, DeviceType.CC);
  }

  /** Trigger device sync and check whether sync was successful */
  public syncWithCallback(deviceId: string, deviceType: DeviceType): Observable<SyncResult> {
    let lastResult: SyncResult;

    let syncObservable: Observable<any> = EMPTY;

    if (deviceType === DeviceType.CB) {
      syncObservable = this.syncCb(deviceId);
    } else if (deviceType === DeviceType.CC) {
      syncObservable = this.syncCc(deviceId);
    }

    return syncObservable.pipe(
      switchMap((response) => {
        return this.getLastSyncBoth(deviceId)
          .pipe(
            map(datetime => {
              const syncDate = dayjs(datetime, 'YYYY-MM-DD HH:mm:ss');
              const diffSeconds = dayjs().diff(syncDate, 'second');

              // If last sync is older than 120 seconds
              if (diffSeconds > 120) {
                return {success: false, lastSync: datetime};
              }
              return {success: true, lastSync: datetime};
            }),
            repeatWhen(notifier => {
              return notifier.pipe(
                takeWhile(() => lastResult.success === false),
                // Max. 10 retries with 1 second gap
                take(10),
                delay(1000)
              );
            }),
            tap(result => lastResult = result),
            // Take last result, no matter whether success or not
            takeLast(1)
          );
      })
    );
  }

  /** Gets date/time of last sync */
  public getLastSyncBoth(deviceId?: string) {
    // both CB and CC
    const offset = new Date().getTimezoneOffset() * -1;
    return this.scgHttp.withDevice(deviceId).get<string>(environment.apiUrl + 'info/getlastsync/' + offset);
  }


  public hasTokenIssue() {
    return this.scgHttp.get<any>(environment.apiUrl + 'cbsync/hasTokenIssue');
  }

  public syncResetCB() {
    return this.scgHttp.get<{ any }>(environment.apiUrl + 'cbsync/mastersync');
  }

  public syncResetCC() {
    return this.scgHttp.get<{ any }>(environment.apiUrl + 'ccsync/mastersync');
  }

  public sessionCheck() {
    return this.scgHttp.get<{ any }>(environment.apiUrl + 'action/sessioncheck');
  }
}

export interface SyncResult {
  /** Whether sync was successful (to be exact, whether the device was synced within the last two minutes) */
  success: boolean;
  /** Last sync date in the form YYYY-MM-DD HH:mm:ss */
  lastSync: string;
}
